import { useCallback, useEffect } from 'react';
import { shouldIgnoreKeyboardShortcut } from '@shared/helpers/event.helpers';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import { getCutoutShapes } from '@shared/helpers/design/cutout.helpers';
import {
  getStoreyBelow,
  getStoreyIndex,
  isStoreyRemovalBlockedByMezzanine,
} from '@shared/helpers/design/storey.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { Storey, STOREY_MIN_HEIGHT } from '@shared/models/design/storey.model';
import amplitudeLog from '@client/amplitude';
import {
  AMP_DESIGN_STOREY_DELETE,
  AMP_DESIGN_STOREY_MEZZANINE_TOGGLE,
} from '@shared/constants';
import { toggleMezzanineStorey } from './mezzanine-utils.helpers';
import {
  useRemoveStorey,
  useDeselectBuilding,
  useSelectedStorey,
  usePanelStoreys,
  useSelectBuilding,
  useUpdateStoreys,
} from '../hooks/storeys.hooks';
import {
  getHasSelectedShape,
  getHasSelectedVertices,
  useHasVisibleBuildingSelection,
  useIsBuildingOutlineSelected,
  useSelectBuildingOutline,
  useSelectedBuildingId,
  useSetSelectedStoreyId,
} from '../store/design-store.hooks';
import { useDesignStore } from '../store/design.store';
import { shouldHandleStoreyDeleteShortcut } from './storey-keyboard-shortcuts.helpers';
import { ignoreDesignSnapshotSuperseded } from '../helpers/design-snapshot-persist.helpers';
import {
  getStoreyElevationInputId,
  getStoreyHeightInputId,
  getStoreyMezzanineElevationInputId,
} from './storey-settings-input-ids.helpers';
import { getBuildingOpenStoreyId } from '../helpers/storey-selection.helpers';
import { useBuildingDesign } from '../hooks/design.hooks';

type UseStoreyKeyboardShortcutsParams = {
  /** Ensures the settings panel for the given storey id is expanded. */
  expandSettingsForStorey: (storeyId: string) => void;
  enabled: boolean;
};

/**
 * DEV-2979: Registers H/E/M hotkeys for storey settings on the Design page,
 * plus Backspace/Delete to remove the selected storey (with undo snackbar).
 *
 * Registers on `window` without calling `stopPropagation` so the map's rxjs
 * observables (also on `window`) continue to receive all keydown events. The
 * listener runs in the capture phase so a key it handles is already marked
 * `defaultPrevented` when the map's own shortcut listener sees it.
 */
export const useStoreyKeyboardShortcuts = ({
  expandSettingsForStorey,
  enabled,
}: UseStoreyKeyboardShortcutsParams): void => {
  const selectedStorey = useSelectedStorey();
  const storeys = usePanelStoreys();
  const updateStoreys = useUpdateStoreys();
  const removeStorey = useRemoveStorey();
  const deselectBuilding = useDeselectBuilding();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const isBuildingOutlineSelected = useIsBuildingOutlineSelected();
  const hasVisibleBuildingSelection = useHasVisibleBuildingSelection();
  const selectedBuildingId = useSelectedBuildingId();
  const selectBuilding = useSelectBuilding();
  const selectBuildingOutline = useSelectBuildingOutline();
  const buildings = useBuildingDesign().buildings;
  const isReadonly = useIsReadonly();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isReadonly) return;
      if (shouldIgnoreKeyboardShortcut(event)) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat)
        return;

      if (
        !hasVisibleBuildingSelection &&
        !event.shiftKey &&
        event.key === 'Enter'
      ) {
        const firstBuildingId = buildings[0]?.id;
        if (!firstBuildingId) return;
        event.preventDefault();
        selectBuilding(firstBuildingId);
        selectBuildingOutline(firstBuildingId);
        return;
      }

      // `enabled` reflects whether the currently selected building has a
      // completed storey. Selecting the first building must still work from
      // the overview, where there deliberately is no selected storey yet.
      if (!enabled) return;

      if (
        isBuildingOutlineSelected &&
        !event.shiftKey &&
        event.key === 'Enter'
      ) {
        const bottomStoreyId = getBuildingOpenStoreyId(storeys);
        if (!bottomStoreyId) return;
        event.preventDefault();
        setSelectedStoreyId(bottomStoreyId);
        return;
      }

      if (
        isBuildingOutlineSelected &&
        event.shiftKey &&
        event.key === 'Enter'
      ) {
        event.preventDefault();
        deselectBuilding();
        return;
      }

      if (
        !isBuildingOutlineSelected &&
        event.shiftKey &&
        event.key === 'Enter'
      ) {
        const buildingId =
          buildings.find((building) => building.id === selectedBuildingId)
            ?.id ??
          (selectedBuildingId === undefined ? buildings[0]?.id : undefined);
        if (!selectedStorey || !buildingId) return;
        event.preventDefault();
        selectBuildingOutline(buildingId);
        return;
      }

      if (isBuildingOutlineSelected && event.key === 'Tab') {
        if (!buildings.length) return;
        const currentIndex = buildings.findIndex(
          (building) => building.id === selectedBuildingId,
        );
        const direction = event.shiftKey ? -1 : 1;
        const nextIndex =
          (Math.max(currentIndex, 0) + direction + buildings.length) %
          buildings.length;
        const nextBuildingId = buildings[nextIndex]?.id;
        if (!nextBuildingId) return;
        event.preventDefault();
        selectBuilding(nextBuildingId);
        selectBuildingOutline(nextBuildingId);
        return;
      }

      // A whole-building selection has no active storey: `useSelectedStorey`
      // still falls back to the ground floor, so every storey shortcut has to
      // stand down and leave the keys (Backspace/Delete included) to the
      // building itself.
      if (isBuildingOutlineSelected) return;

      // Shift+Tab: select storey below, wrap from bottom to top.
      if (event.key === 'Tab' && event.shiftKey) {
        if (!storeys.length) return;
        event.preventDefault();
        const currentIndex = storeys.findIndex(
          (s) => s.id === selectedStorey?.id,
        );
        const nextIndex =
          currentIndex <= 0 ? storeys.length - 1 : currentIndex - 1;
        setSelectedStoreyId(storeys[nextIndex]?.id);
        return;
      }

      if (!selectedStorey) return;

      // Backspace/Delete removes the selected storey unless a shape, its
      // vertices, the whole building, or a PDF underlay are selected (those
      // own the keys).
      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (
          !shouldHandleStoreyDeleteShortcut({
            isBuildingOutlineSelected,
            hasSelectedVertices: getHasSelectedVertices(),
            hasSelectedShape: getHasSelectedShape(),
            isBlueprintUnderlaySelected:
              useDesignStore.getState().blueprintInteractionMode !== 'locked',
          })
        ) {
          return;
        }
        // Mirror the row's delete affordance: the ground floor stays while a
        // mezzanine sits above it.
        if (isStoreyRemovalBlockedByMezzanine(storeys, selectedStorey)) return;
        event.preventDefault();
        void ignoreDesignSnapshotSuperseded(removeStorey(selectedStorey.id));
        amplitudeLog(AMP_DESIGN_STOREY_DELETE, { StoreyId: selectedStorey.id });
        return;
      }

      const k = event.key.toLowerCase();
      if (k !== 'm' && k !== 'h' && k !== 'e') return;

      event.preventDefault();

      if (k === 'm') {
        handleMezzanineToggle({
          storey: selectedStorey,
          storeys,
          updateStoreys,
        });
        return;
      }

      expandSettingsForStorey(selectedStorey.id);
      const inputId = resolveFocusInputId(
        storeys,
        selectedStorey,
        k as 'h' | 'e',
      );
      focusInputById(inputId);
    },
    [
      enabled,
      buildings,
      deselectBuilding,
      hasVisibleBuildingSelection,
      isBuildingOutlineSelected,
      isReadonly,
      removeStorey,
      selectBuilding,
      selectBuildingOutline,
      selectedBuildingId,
      selectedStorey,
      storeys,
      updateStoreys,
      setSelectedStoreyId,
      expandSettingsForStorey,
    ],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);
};

const resolveFocusInputId = (
  storeys: Storey[],
  storey: Storey,
  key: 'h' | 'e',
): string => {
  if (designGuards.is.mezzanineStorey(storey)) {
    return getStoreyMezzanineElevationInputId(storey.id);
  }
  if (getStoreyIndex(storeys, storey) === 0) {
    return key === 'h'
      ? getStoreyHeightInputId(storey.id)
      : getStoreyElevationInputId(storey.id);
  }
  return getStoreyHeightInputId(storey.id);
};

/** Two rAF ticks ensures the InsetPanel expand animation has started before focusing. */
const focusInputById = (inputId: string): void => {
  const run = () => {
    const el = document.getElementById(inputId);
    const input =
      el instanceof HTMLInputElement
        ? el
        : (el?.querySelector?.('input') ?? null);
    if (!(input instanceof HTMLInputElement)) return;
    input.focus({ preventScroll: true });
    input.select();
  };
  requestAnimationFrame(() => requestAnimationFrame(run));
};

const handleMezzanineToggle = ({
  storey,
  storeys,
  updateStoreys,
}: {
  storey: Storey;
  storeys: Storey[];
  updateStoreys: (
    ...updates: import('@shared/models/type-helpers.model').SemiPartial<
      Storey,
      'id'
    >[]
  ) => void | Promise<unknown>;
}): void => {
  const isGroundFloor = storeys[0]?.id === storey.id;
  if (isGroundFloor) return;

  const parentStorey = getStoreyBelow(storeys, storey);
  const parentStoreyHeight = parentStorey?.height;
  const parentHasCutouts =
    Boolean(parentStorey) && getCutoutShapes(parentStorey!).length > 0;

  // Mirror disabled conditions from StoreySettingsPanel mezzanine switch.
  if (
    !guards.is.number(parentStoreyHeight) ||
    parentStoreyHeight < STOREY_MIN_HEIGHT * 2 ||
    (!storey.mezzanine && parentHasCutouts)
  ) {
    return;
  }

  const isMezzanine = designGuards.is.mezzanineStorey(storey);

  if (isMezzanine) {
    // Turn off mezzanine — revert to plain storey using the same logic as StoreySettingsPanel.
    void ignoreDesignSnapshotSuperseded(
      updateStoreys({
        id: storey.id,
        mezzanine: false,
        mezzanine_elevation: undefined,
      }),
    );
    amplitudeLog(AMP_DESIGN_STOREY_MEZZANINE_TOGGLE, {
      StoreyId: storey.id,
      Mezzanine: false,
    });
    return;
  }

  const fallback = Math.max(
    parentStoreyHeight - STOREY_MIN_HEIGHT,
    parentStoreyHeight / 2,
  );
  const nextStorey = toggleMezzanineStorey({
    storey,
    storeys,
    name: undefined,
    mezzanineElevationDraft: fallback,
  });
  if (!nextStorey) return;
  void ignoreDesignSnapshotSuperseded(updateStoreys(nextStorey));
  amplitudeLog(AMP_DESIGN_STOREY_MEZZANINE_TOGGLE, {
    StoreyId: storey.id,
    Mezzanine: true,
  });
};
